import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    StatusBar,
    Platform,
    Image,
    Animated,
    TextInput,
    KeyboardAvoidingView,
} from 'react-native';
import { usePIN } from '../../contexts/PINContext';
import { useAuth } from '../../contexts/AuthContext';
import { getLockoutDuration } from '../../services/pin';
import {
    getSupportedBiometryType,
    getBiometryDisplayName,
    getBiometricCredentials,
    BiometryType,
} from '../../services/biometrics';
import { COLORS } from '../../theme/themeTokens';
import { COLORS_WRONG } from '../../theme/colorsWrong';
import PINDots from '../../components/UIComponents/PINDots';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';

const PIN_LENGTH = 4;

const PINUnlockScreen: React.FC = () => {
    const statusBarHeight = Platform.OS === 'android' ? 24 : 47;

    const { verifyPIN, unlockApp, clearPIN, failedAttempts, isLockedOut, getRemainingLockoutTime } = usePIN();
    const { biometricsEnabled, logout } = useAuth();

    const [pin, setPin] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [lockoutTime, setLockoutTime] = useState(0);
    const [biometryType, setBiometryType] = useState<BiometryType>(null);

    const shakeAnimation = useRef(new Animated.Value(0)).current;
    const inputRef = useRef<TextInput>(null);

    // Check biometry type on mount
    useEffect(() => {
        const checkBiometry = async () => {
            const type = await getSupportedBiometryType();
            setBiometryType(type);
        };
        checkBiometry();
    }, []);

    // Auto-trigger biometric on mount if enabled
    useEffect(() => {
        if (biometricsEnabled && biometryType) {
            // Small delay to let screen render
            const timer = setTimeout(() => {
                handleBiometricUnlock();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [biometricsEnabled, biometryType]);

    // Update lockout timer
    useEffect(() => {
        if (!isLockedOut()) return;

        let interval: ReturnType<typeof setInterval>;

        const updateTimer = () => {
            const remaining = getRemainingLockoutTime();
            setLockoutTime(remaining);
            if (remaining <= 0) {
                clearInterval(interval);
                setError(null);
                setPin('');
                // Blur then focus forces Android to properly re-register
                // onChangeText after editable was restricted
                setTimeout(() => {
                    inputRef.current?.blur();
                    setTimeout(() => inputRef.current?.focus(), 50);
                }, 100);
            }
        };

        updateTimer();
        interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [failedAttempts, isLockedOut, getRemainingLockoutTime]);

    const shake = () => {
        Animated.sequence([
            Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true }),
        ]).start();
    };

    const handlePinChange = async (value: string) => {
        if (isLoading || isLockedOut()) return;

        const digits = value.replace(/[^0-9]/g, '').slice(0, PIN_LENGTH);
        setError(null);
        setPin(digits);

        if (digits.length === PIN_LENGTH) {
            setIsLoading(true);
            const isValid = await verifyPIN(digits);
            setIsLoading(false);

            if (!isValid) {
                shake();

                // Compute lockout based on new attempt count directly,
                // because context setState is async and lockoutUntil is still stale here
                const newAttempts = failedAttempts + 1;
                const lockoutDuration = getLockoutDuration(newAttempts);

                if (lockoutDuration > 0) {
                    setLockoutTime(Math.ceil(lockoutDuration / 1000));
                    setError(null);
                } else {
                    setError('Incorrect PIN');
                }
                setPin('');
                setTimeout(() => inputRef.current?.focus(), 100);
            }
            // If valid, verifyPIN already unlocks the app via context
        }
    };

    const handleBiometricUnlock = async () => {
        if (!biometricsEnabled || !biometryType) return;

        try {
            setIsLoading(true);
            setError(null);

            // Get biometric credentials (this will prompt for biometric)
            const credentials = await getBiometricCredentials();

            if (credentials) {
                // If biometric auth succeeded, unlock the app
                unlockApp();
            }
        } catch (err) {
            console.log('Biometric unlock failed:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoginWithPassword = async () => {
        await clearPIN();
        await logout();
    };



    return (
        <KeyboardAvoidingView
            style={styles.container}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>

            {/* Hidden system keyboard input */}
            <TextInput
                ref={inputRef}
                value={pin}
                onChangeText={handlePinChange}
                keyboardType="number-pad"
                maxLength={PIN_LENGTH}
                autoFocus
                editable={!isLoading}
                style={styles.hiddenInput}
                caretHidden
                {...PIN_INPUT_PROPS}
            />

            {/* Header */}
            <View style={[styles.headerBackground, { paddingTop: statusBarHeight }]}>
                {/* Blue glow effects */}
                {/* <Image
                    source={require('../../assets/blur-left.png')}
                    style={styles.blurLeft}
                />
                <Image
                    source={require('../../assets/blur-right.png')}
                    style={styles.blurRight}
                /> */}
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {/* Icon */}
                <View style={styles.iconContainer}>
                   <Image source={require('../../assets/Secure-lock.png')} style={{ width: 80, height: 80 }} resizeMode="contain" />
                </View>

                {/* Title */}
                <Text style={styles.title}>Enter PIN</Text>
                <Text style={styles.subtitle}>
                    {lockoutTime > 0
                        ? `Try again in ${lockoutTime} seconds`
                        : 'Enter 4 numbers to keep your account safe'}
                </Text>

                {/* PIN Dots */}
                <PINDots
                    pin={pin}
                    pinLength={PIN_LENGTH}
                    error={error}
                    shakeAnimation={shakeAnimation}
                    onPress={() => inputRef.current?.focus()}
                />

                {/* Error Message */}
                {error && lockoutTime === 0 && (
                    <Text style={styles.errorText}>{error}. You have {Math.max(0, 4 - failedAttempts + 1)} attempts remaining.</Text>
                )}

                {/* Failed Attempts Counter */}
                {/* {failedAttempts > 0 && failedAttempts <= 5 && (
                    <Text style={styles.attemptsText}>
                        {5 - failedAttempts} attempts remaining
                    </Text>
                )} */}

                {/* Biometric unlock */}
                {biometricsEnabled && biometryType && (
                    <View style={styles.biometricContainer}>
                        <TouchableOpacity
                            style={styles.bioButton}
                            onPress={handleBiometricUnlock}
                            disabled={isLoading || isLockedOut()}>
                            <Image
                                source={require('../../assets/profile-faceid.png')}
                                style={styles.bioIcon}
                            />
                        </TouchableOpacity>
                        <Text style={styles.biometricHint}>
                            Use {getBiometryDisplayName(biometryType)} to unlock
                        </Text>
                    </View>
                )}
            </View>

            <PinKeypad
                value={pin}
                length={PIN_LENGTH}
                onChange={handlePinChange}
                disabled={isLoading || lockoutTime > 0}
            />

            {/* Login with username/password link */}
            <TouchableOpacity
                style={styles.loginLinkContainer}
                onPress={handleLoginWithPassword}
                disabled={isLoading}>
                <Text style={styles.loginLinkText}>Forgot PIN</Text>
            </TouchableOpacity>
        </KeyboardAvoidingView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.white,
    },
    headerBackground: {
        backgroundColor: COLORS.white,
        paddingBottom: 20,
        overflow: 'hidden',
        paddingHorizontal: 20,
    },
    blurLeft: {
        position: 'absolute',
        width: 208,
        height: 208,
        left: -64,
        top: 40,
    },
    blurRight: {
        position: 'absolute',
        width: 208,
        height: 208,
        right: -64,
        top: 40,
    },
    mainContent: {
        flex: 1,
        backgroundColor: COLORS.white,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        paddingTop: 40,
        alignItems: 'center',
    },
    iconContainer: {
        marginBottom: 24,
        width: 92,
        height: 92,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible',
    },
    lockIcon: {
        width: 85,
        height: 85,
        borderRadius: 40,
        backgroundColor: COLORS.white,
        justifyContent: 'center',
        alignItems: 'center',
    },
    lockIconText: {
        fontSize: 36,
    },
    title: {
        fontFamily: 'Inter',
        fontSize: 26,
        fontWeight: '600',
        color: COLORS.brandOrange,
        marginBottom: 8,
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.deepNavy,
        textAlign: 'center',
        marginBottom: 32,
        paddingHorizontal: 40,
    },

    errorText: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS_WRONG.error,
        textAlign: 'center',
        marginBottom: 8,
    },
    attemptsText: {
        fontFamily: 'Inter',
        fontSize: 14,
        color: COLORS.blueSteel,
        textAlign: 'center',
        marginBottom: 16,
    },
    hiddenInput: {
        position: 'absolute',
        opacity: 0,
        width: 1,
        height: 1,
    },
    biometricContainer: {
        alignItems: 'center',
        marginTop: 32,
    },
    bioButton: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: COLORS.steelNavy,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 12,
    },
    bioIcon: {
        width: 28,
        height: 28,
        tintColor: COLORS.white,
    },
    biometricHint: {
        fontFamily: 'Inter',
        fontSize: 14,
        color: COLORS.blueSteel,
        textAlign: 'center',
        marginTop: 16,
    },
    loginLinkContainer: {
        paddingVertical: 20,
        alignItems: 'center',
    },
    loginLinkText: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.deepNavy,
        textDecorationLine: 'underline',
    },
});

export default PINUnlockScreen;

